import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useClipboard } from '@vueuse/core';
import { useStore } from 'vuex';
import { useAbility } from '@casl/vue';
import { useQueryClient } from '@tanstack/vue-query';
import {
  usePostAccountShipmentsBulkDelete,
  usePostAccountShipmentPublish,
} from '@gpx-spa/queries';
import getAccountShipmentV2 from '@gpx-spa/queries/src/getAccountShipmentV2';
import type { V2ShipmentDetail } from '@gpx-spa/queries/src/getAccountShipmentV2';
import { useShipmentListActions } from '@/composables/useShipmentListActions';
import { COMMON_DELETE_ERROR, COMMON_DELETE_SUCCESS } from '@/config/constants';
import { useToast } from 'vue-toastification';
import {
  isDraftShipment,
  canEditShipmentForUser,
} from '@/utils/shipment-draft-access';
import { getLaravelValidationErrorsFromAxios } from '@/utils/laravel-validation-errors';

function publishErrorMessage(err: unknown): string {
  const laravel = getLaravelValidationErrorsFromAxios(err);
  if (laravel) {
    for (const messages of Object.values(laravel)) {
      const first = messages?.[0];
      if (first) return first;
    }
  }
  return (
    (err as { response?: { data?: { message?: string } } })?.response?.data
      ?.message ??
    (err as Error)?.message ??
    'Could not publish shipment.'
  );
}

export function useShipmentDetailActions(
  accountId: () => number,
  shipment: () => V2ShipmentDetail | null | undefined,
) {
  const router = useRouter();
  const store = useStore();
  const { can } = useAbility();
  const toast = useToast();
  const queryClient = useQueryClient();

  const listActions = useShipmentListActions({});
  const bulkDelete = usePostAccountShipmentsBulkDelete();
  const publishMutation = usePostAccountShipmentPublish();

  const canManage = computed(() => can('manage', 'Shipments'));

  const isDraft = computed(() => {
    const s = shipment();
    return s ? isDraftShipment(s) : false;
  });

  const isClosed = computed(
    () => String(shipment()?.status ?? '').toLowerCase() === 'closed',
  );

  const canShowPublish = computed(() => {
    const s = shipment();
    if (!s || !canManage.value || !isDraft.value) return false;
    return canEditShipmentForUser(
      s as V2ShipmentDetail & { archived_at?: string | null },
      true,
    );
  });

  const canShowClose = computed(
    () => Boolean(shipment()) && canManage.value && !isDraft.value && !isClosed.value,
  );

  const publishBusy = computed(() => publishMutation.isPending.value);

  const shareUrl = computed(() => {
    const uuid = shipment()?.uuid;
    if (!uuid) return null;
    return `${window.location.origin}/shipments/track/${uuid}`;
  });

  function shareLink() {
    const url = shareUrl.value;
    if (!url) {
      toast.error('Share link is not available for this shipment.');
      return;
    }
    const { copy, copied } = useClipboard({ source: url });
    copy();
    if (copied.value) {
      toast.success('Link copied!', { timeout: 800 });
    } else {
      toast.success('Link copied!', { timeout: 800 });
    }
  }

  function editShipment() {
    const s = shipment();
    if (!s) return;
    listActions.editShipment(s);
  }

  function closeShipment() {
    const s = shipment();
    if (!s || !canShowClose.value) return;
    listActions.confirmClose([s.id]);
  }

  async function publishShipment() {
    const s = shipment();
    if (!s || !canShowPublish.value || publishBusy.value) return;

    try {
      await publishMutation.mutateAsync({
        accountId: accountId(),
        shipmentId: s.id,
      });
      toast.success('Shipment published.');
    } catch (err: unknown) {
      toast.error(publishErrorMessage(err));
    }
  }

  function deleteShipment() {
    const s = shipment();
    if (!s || !canManage.value) return;
    store.dispatch('modal/open', {
      name: 'CommonConfirmationModal',
      params: {
        actionLabel: 'Delete',
        title: 'Shipment',
        body: 'Are you sure you want to delete this shipment?',
        onOk: async () => {
          await bulkDelete.mutateAsync({
            accountId: accountId(),
            ids: [s.id],
          });
          await queryClient.invalidateQueries({
            queryKey: ['account-shipments'],
          });
          router.push({ name: 'ShipmentListView' });
        },
        successMessage: COMMON_DELETE_SUCCESS,
        errorMessage: COMMON_DELETE_ERROR,
      },
    });
  }

  async function invalidateShipment() {
    const aid = accountId();
    const sid = shipment()?.id;
    if (!aid || !sid) return;
    await queryClient.invalidateQueries({
      queryKey: getAccountShipmentV2.queryKey(aid, sid),
    });
  }

  return {
    canManage,
    canShowPublish,
    canShowClose,
    publishBusy,
    shareUrl,
    shareLink,
    editShipment,
    closeShipment,
    publishShipment,
    deleteShipment,
    invalidateShipment,
  };
}
